import {Color} from '@benchling/nucleus-ui';
import {createStyles, Group, Text, useMantineTheme} from '@mantine/core';
import React, {useEffect, useRef, useState} from 'react';
import {Node, NodeProps, useUpdateNodeInternals} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {
  COMMON_NODE_WIDTH,
  NODE_HOVER_BOX_SHADOW,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

import {getConditionNameSuffix} from '../../procedure-runs/conditions/conditionArchiveDisplayUtils';
import type {VaryingInputSection} from '../../procedure-runs/design-tabs/FactorVariationUtils';
import {
  getStatusHeaderColor,
  getStatusIcon,
} from '../../procedure-runs/execution-status/unitOperationStatusVisuals';
import type {UnitOperationStatusType} from '../../procedure-runs/overview/procedureRunOverviewUtils';
import type {ConnectionCapabilities} from './procedureRunDesignFlowchartConnectionUtils';
import {useProcedureRunDesignFlowchartIsEditable} from './procedureRunDesignFlowchartEditableContext';
import {
  getSourceHandleAffordance,
  ProcedureRunDesignFlowchartNodeHandle,
  ProcedureRunDesignFlowchartNodeHandleVariant,
  ProcedureRunDesignFlowchartNodeHoverGutters,
} from './ProcedureRunDesignFlowchartNodeHandle';
import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';
import ProcedureRunDesignFlowchartNodeSections from './ProcedureRunDesignFlowchartNodeSections';
import ProcedureRunDesignFlowchartNodeToolbar, {
  NODE_TOOLBAR_OFFSET_PX,
} from './ProcedureRunDesignFlowchartNodeToolbar';
import {useProcedureRunDesignFlowchartShowArchived} from './procedureRunDesignFlowchartShowArchivedContext';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE = 'procedureRunDesignFlowchartNode';

/** Matches COMMON_NODE_WIDTH used by BenchlingNode, which is set as the node's rendered width. */
export const FALLBACK_NODE_WIDTH = COMMON_NODE_WIDTH;
/** Estimated height of a node before ReactFlow measures it. Approximates a node with no description. */
export const FALLBACK_NODE_HEIGHT = 32;
const NO_CONNECTION_CAPABILITIES: ConnectionCapabilities = {source: false, target: false};
/** The node fields that depend on a cell's archive state. */

export interface CellPresentationFields {
  isMethodHovered: boolean;
  isHighlighted: boolean;
  // True when this specific node is hovered. isMethodHovered tracks hover state for the entire method column. This
  // per-node hover uses the same ReactFlow onNodeMouseEnter/onNodeMouseLeave events as isMethodHovered, so it
  // stays in sync.
  isNodeHovered: boolean;
  // True when this node's condition is persisted and read-only (e.g. editing an existing procedure run).
  // Drives the lock icon in the node header.
  isLocked: boolean;
  /** True when this cell (PMEI) is archived (shown only when "Show archived" is on). */
  isArchived?: boolean;
  /** Human-readable archive reason from `archiveData.purpose`, shown under the title when archived. */
  archiveReason?: string;
  // Leave undefined when statuses should not be rendered
  status?: UnitOperationStatusType;
  // This node's source/target abilities in the upstream->active draw affordance.
  connectionCapabilities: ConnectionCapabilities;
  // Whether this node's source handle should stay visible at rest (not just on hover). Edge-dependent,
  // only meaningful when the node has source capability; a source node always shows its handle on hover.
  showPersistentSourceHandle: boolean;
}

export interface ProcedureRunDesignFlowchartNodeData extends CellPresentationFields {
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  methodId: string;
  // Defaults to 'subtle' (transparent) when omitted.
  handleVariant?: ProcedureRunDesignFlowchartNodeHandleVariant;
  isInActiveMethod: boolean;
  isPendingRemoval: boolean;
  isRemoved: boolean;
  varyingInputSections?: Array<VaryingInputSection>;
}

export function createProcedureRunDesignFlowchartNode({
  conditionIndex,
  replicateIndex,
  methodId,
  conditionName,
  replicateNumber,
  conditionDescription,
  isArchived,
  archiveReason,
  isMethodHovered,
  isHighlighted,
  isLocked,
  status,
  connectionCapabilities = NO_CONNECTION_CAPABILITIES,
  showPersistentSourceHandle = false,
  isInActiveMethod,
  varyingInputSections,
}: {
  conditionIndex: number;
  replicateIndex: number;
  methodId: string;
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  isArchived?: boolean;
  archiveReason?: string;
  isMethodHovered: boolean;
  isHighlighted: boolean;
  isLocked: boolean;
  status?: UnitOperationStatusType;
  connectionCapabilities?: ConnectionCapabilities;
  showPersistentSourceHandle?: boolean;
  isInActiveMethod: boolean;
  varyingInputSections?: Array<VaryingInputSection>;
}): Node<ProcedureRunDesignFlowchartNodeData> {
  return {
    id: getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId}),
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
    data: {
      conditionName,
      replicateNumber,
      conditionDescription,
      methodId,
      isArchived,
      archiveReason,
      isMethodHovered,
      isHighlighted,
      isNodeHovered: false,
      isLocked,
      status,
      connectionCapabilities,
      showPersistentSourceHandle,
      isInActiveMethod,
      isPendingRemoval: false,
      isRemoved: false,
      varyingInputSections,
    },
    position: {
      x: 0,
      y: 0,
    },
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE;
}

export default function ProcedureRunDesignFlowchartNode(
  props: NodeProps<ProcedureRunDesignFlowchartNodeData>
): JSX.Element {
  const {classes, cx} = useStyles();
  const theme = useMantineTheme();
  const {data, id} = props;
  const {
    isMethodHovered,
    isHighlighted,
    isNodeHovered,
    isLocked,
    status,
    connectionCapabilities,
    showPersistentSourceHandle,
    isInActiveMethod,
    isPendingRemoval,
    isRemoved,
    isArchived = false,
    archiveReason,
  } = data;

  const showArchived = useProcedureRunDesignFlowchartShowArchived();
  // A condition replicate node removed in this edit session previews the archived treatment it will take once saved, but
  // only while showArchived is enabled. Otherwise it stays hidden in place, as before.
  // `isRemoved` is itself the not-yet-persisted signal: a removed node id is derived only while its cell
  // is still live, so once refetched conditions render it as archived the two states hand off in one render.
  const isPendingArchive = !isArchived && isRemoved && showArchived;
  // Persisted-archived and pending-archive nodes share every inert visual; only the text copy differs.
  const isInert = isArchived || isPendingArchive;
  const sourceAffordance = getSourceHandleAffordance(status);

  // The active-layer handles mount based on the node's capabilities and edit mode but float without changing
  // the node's measured size, so ReactFlow won't re-measure on its own. Refresh the handle geometry
  // whenever that set changes, otherwise edges and the drag preview line can't anchor to the new handle.
  const isEditable = useProcedureRunDesignFlowchartIsEditable();
  const updateNodeInternals = useUpdateNodeInternals();
  // Edit mode only flips the rendered handle set for source/target-capable nodes. Nodes with neither
  // capability always render just the flush handle, so keep their key constant to avoid needless re-measures.
  const handleConfigKey =
    connectionCapabilities.source || connectionCapabilities.target
      ? `${connectionCapabilities.source}:${connectionCapabilities.target}:${isEditable}`
      : 'none';
  const prevHandleConfigKeyRef = useRef(handleConfigKey);
  useEffect(() => {
    if (prevHandleConfigKeyRef.current !== handleConfigKey) {
      updateNodeInternals(id);
      prevHandleConfigKeyRef.current = handleConfigKey;
    }
  }, [handleConfigKey, id, updateNodeInternals]);

  // Archived cells are inert and can't be removed, so they never surface the remove-replicate toolbar.
  const toolbarEnabledForNode = isEditable && isInActiveMethod && !isRemoved && !isInert;
  // NodeToolbar renders via a ReactFlow portal outside the node's DOM wrapper, so ReactFlow fires
  // onNodeMouseLeave when the cursor moves from the node into the toolbar. This state keeps the
  // toolbar visible while the cursor is on it.
  const [isNodeToolbarHovered, setIsNodeToolbarHovered] = useState(false);
  const showNodeToolbar = toolbarEnabledForNode && (isNodeHovered || isNodeToolbarHovered);
  const isHoveredOrHighlighted = !isRemoved && (isMethodHovered || isHighlighted);
  // Archived cells are inert: they render greyed out and ignore hover/highlight and status tinting.
  // Otherwise hover/highlight tints the whole node while the status color tints only the header.
  const rootBg = isInert ? theme.colors.gray[1] : isHoveredOrHighlighted ? theme.colors.blue[0] : theme.white;
  const headerBg = isInert
    ? theme.colors.gray[1]
    : isHoveredOrHighlighted
      ? theme.colors.blue[0]
      : getStatusHeaderColor(status ?? null);
  const {icon: statusIcon, iconColor: statusIconColor} = getStatusIcon(status ?? null);

  return (
    <>
      <ProcedureRunDesignFlowchartNodeToolbar
        isVisible={showNodeToolbar}
        nodeId={id}
        onToolbarMouseEnter={() => {
          setIsNodeToolbarHovered(true);
        }}
        onToolbarMouseLeave={() => {
          setIsNodeToolbarHovered(false);
        }}
      />
      <BenchlingNode
        isSelectable={!isInert}
        isSelected={!isInert && !isRemoved && !isPendingRemoval && isHighlighted}
        w="200px"
        data-pending-removal={isPendingRemoval && !isRemoved ? true : undefined}
        data-pending-archive={isPendingArchive ? true : undefined}
        sx={(theme) => ({
          backgroundColor: rootBg,
          ...(isPendingRemoval && !isRemoved && {borderColor: theme.colors.red[8]}),
          ...(isMethodHovered && !isRemoved && !isInert && {boxShadow: NODE_HOVER_BOX_SHADOW}),
          // A removed cell previewing its archived state stays visible and greyed; otherwise a removed
          // cell holds its grid slot invisibly so the layout doesn't reflow mid-edit.
          ...(isRemoved && !isPendingArchive && {visibility: 'hidden', pointerEvents: 'none'}),
        })}
        dataTestAttrs={{
          element: 'root',
          key: props.id,
        }}
      >
        {!isRemoved && (
          <>
            <ProcedureRunDesignFlowchartNodeHoverGutters />
            {/* Bridges the gap between the node top edge and the NodeToolbar so the cursor can travel
              to the node toolbar without onNodeMouseLeave firing and hiding it. */}
            {showNodeToolbar && (
              <span
                aria-hidden={true}
                className={classes.topHoverGutter}
                {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'topHoverGutter'})}
              />
            )}
            {/* Each slot delegates to the flush handle or the active-layer connection handle by capability. */}
            <ProcedureRunDesignFlowchartNodeHandle
              nodeId={id}
              handleType="target"
              connectionCapabilities={connectionCapabilities}
              showPersistentSourceHandle={false}
              isNodeHovered={isNodeHovered}
              variant={data.handleVariant}
            />
            <ProcedureRunDesignFlowchartNodeHandle
              nodeId={id}
              handleType="source"
              connectionCapabilities={connectionCapabilities}
              showPersistentSourceHandle={showPersistentSourceHandle}
              isNodeHovered={isNodeHovered}
              sourceAffordance={sourceAffordance}
              variant={data.handleVariant}
            />
          </>
        )}
        <BenchlingNode.Header
          bg={headerBg}
          {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'header'})}
        >
          <Group spacing="sm" align="flex-start" noWrap={true}>
            <Group spacing={theme.spacing.xs} align="center" noWrap={true} className={classes.nameAndLock}>
              {(isLocked || isPendingArchive) && (
                <BenchlingIcon
                  icon="lock"
                  className={classes.lockIcon}
                  {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'lockIcon'})}
                />
              )}
              <Text className={cx(classes.conditionReplicate, isInert && classes.archivedText)}>
                {`${data.conditionName} - ${data.replicateNumber}${getConditionNameSuffix({isArchived, isPendingArchive})}`}
              </Text>
            </Group>
            {statusIcon && !isPendingArchive && (
              <BenchlingIcon
                icon={statusIcon}
                className={classes.statusIcon}
                style={{color: statusIconColor}}
              />
            )}
          </Group>
          {data.conditionDescription ? (
            <Text className={classes.description}>{data.conditionDescription}</Text>
          ) : null}
          {isArchived && archiveReason ? (
            <Text
              className={classes.archiveReason}
              {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'archiveReason'})}
            >
              {archiveReason}
            </Text>
          ) : null}
        </BenchlingNode.Header>
        <ProcedureRunDesignFlowchartNodeSections
          isInert={isInert}
          varyingInputSections={data.varyingInputSections}
        />
      </BenchlingNode>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  // Transparent region above the node that bridges the gap between the node top edge and the
  // NodeToolbar, so the cursor can move to the toolbar without triggering onNodeMouseLeave and hiding it.
  topHoverGutter: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -NODE_TOOLBAR_OFFSET_PX,
    height: NODE_TOOLBAR_OFFSET_PX,
    pointerEvents: 'auto',
  },
  nameAndLock: {
    flex: 1,
    minWidth: 0,
  },
  conditionReplicate: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    lineHeight: '16px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  description: {
    color: Color.GRAY_600,
    fontSize: theme.fontSizes.xxs,
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  archivedText: {
    color: Color.GRAY_600,
  },
  archiveReason: {
    color: Color.GRAY_600,
    fontStyle: 'italic',
    fontSize: theme.fontSizes.xxs,
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  statusIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  lockIcon: {
    width: 8,
    height: 8,
    flexShrink: 0,
    color: Color.GRAY_600,
  },
}));
